import {inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {StateService} from "@uirouter/core";
import * as _ from "lodash";

import {ParametersService} from '../parameters.service';
import {ModuleData, Settings} from "../../models";
import {UserService} from "../user.service";
import {ManualsService} from "./manuals.service";
import {SettingsTilesService} from "./settings-tiles.service";
import {FormsService} from "../forms.service";
import {ViewsService} from "../views.service";
import {SubtitlesService} from "../subtitles.service";
import {BehaviorSubject} from "rxjs";
import {AbstractControl, ValidationErrors, ValidatorFn} from "@angular/forms";


@Injectable({
    providedIn: 'root'
})
export class SettingsService {
    private http = inject(HttpClient);
    private forms = inject(FormsService);
    private manuals = inject(ManualsService);
    private parameters = inject(ParametersService);
    private settingsTiles = inject(SettingsTilesService);
    private state = inject(StateService);
    private subtitles = inject(SubtitlesService);
    private user = inject(UserService);
    private views = inject(ViewsService);

    lastUpdate: string = '';
    modules: ModuleData[] = [];
    savedModuleData: boolean = false;
    selectedModule: ModuleData | undefined = undefined;
    selectedModule$ = new BehaviorSubject<ModuleData | undefined>(undefined);
    showCard: boolean = false;
    showMobileMenu: boolean = true;
    title: string = '';
    clickOutsideToClose = new BehaviorSubject<boolean>(false);
    clickOutsideToClose$ = this.clickOutsideToClose.asObservable();

    setModulesInformation(): void {
        this.modules = _.cloneDeep(this.parameters.modules);
        this.selectedModule = _.cloneDeep(this.parameters.modules[this.parameters.selectedModuleIndex]);
        this.selectedModule$.next(this.selectedModule);
        if (this.selectedModule !== undefined) {
            this.parameters.selectedSettingsModuleId = this.selectedModule.id;
        }
    }

    getLastModification(): void {
        this.http.get(`/api/v1/users/${this.user.user_id}/last_modification`, {responseType: 'text'})
            .subscribe((response: string) => {
                this.lastUpdate = response.split('').filter((c: string) => c !=='"').join('');
            });
    }

    switchLayout(page: Settings): void {
        if (!this.state.is(`settings.${page}`)) {
            if (this.forms.showModuleCountry.length === 0) {
                this.forms.getCountryInitial();
            }
            void this.state.go(`settings.${page}`, {selectedModuleHash: this.parameters.selectedModuleHash});
        }
        if (this.views.isDesktopView()) {
            this.title = this.subtitles.setSubtitle(845);
        } else {
            this.showCard = true;
            this.showMobileMenu = false;
            this.setCardTitle(page);
        }
    }

    private setSelectModule(index: number): void {
        if (this.selectedModule) {
            this.manuals.getManualsList(this.selectedModule.type);
            this.parameters.selectedSettingsModuleId = this.selectedModule.id;
            this.settingsTiles.getCurrentSelectedModuleTiles(index);
        }
    };

    getSelectedUdid(): string | 0 {
        let udid = this.parameters.selectedModuleHash;

        if (this.selectedModule && this.selectedModule.udid
            && (this.parameters.selectedModuleHash !== this.selectedModule.udid)) {
            udid = this.selectedModule.udid
        }
        return udid;
    }

    selectModuleByUdid(udid: string): void {
        const index = this.modules.findIndex(module => module.udid === udid);
        if (index === -1) {
            console.error(`Cannot select settings module with udid: ${udid}`);
            return;
        }

        if (this.selectedModule?.udid !== udid) {
            const selectedModule = this.modules[index];
            this.selectedModule = selectedModule;

            if (selectedModule.country === null || selectedModule.country === '') {
                const country = this.forms.getDefaultCountry() ?? { initial: 'en', name: '' };
                this.forms.showModuleCountry[selectedModule.id] = { country: country, show: false };
                selectedModule.country = country.name;
            }

            this.selectedModule$.next(selectedModule);
        }

        this.setSelectModule(index);
    }

    isZonesSingleController(): boolean {
        return this.selectedModule?.type === 'zones_controller'
    }

    isActive(state: Settings): boolean {
        return this.state.is(`settings.${state}`);
   }

    zipCodeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formGroup = control.parent;
            if (!formGroup) return null;

            const country = this.selectedModule?.country ?? '';
            const value = control.value;

            if (!value) return null;

            let regex: RegExp;
            if (country === 'Polska') {
                regex = /^\d{2}[-]\d{3}$/;
            } else {
                regex = /^([a-zA-Z0-9.\-_\s]{3,9})$/;
            }

            return regex.test(value) ? null : { invalidZipCode: true };
        };
    }

   private setCardTitle(page: Settings): void {
        switch (page) {
            case 'account':
                this.title = this.subtitles.setSubtitle(5041);
                break;
            case 'module_info':
                this.title = this.subtitles.setSubtitle(1188);
                break;
            case 'controller_software':
                this.title = this.subtitles.setSubtitle(60);
                break;
            case 'additional_software':
                this.title = 'Additional software';
                break;
            case 'notifications':
                this.title = this.subtitles.setSubtitle(1096);
                break;
            case 'tiles':
                this.title = this.subtitles.setSubtitle(5040);
                break;
            case 'manuals':
                this.title = this.subtitles.setSubtitle(6537);
                break;
            case 'module_options':
                this.title = this.subtitles.setSubtitle(8054);
                break;
            case 'registration_new_module':
                this.title = this.subtitles.setSubtitle(1183);
                break;
            default:
                this.title = this.subtitles.setSubtitle(845);
        }
   }
}
